import {
    Component,
    Input,
    OnDestroy,
    OnChanges,
    OnInit,
    EventEmitter,
    Output,
    Injector,
} from '@angular/core';

import {
    EntityAction,
} from 'breeze-client';
import { of } from 'rxjs';

import { DataManagerService } from '@services/data-manager.service';
import { EntityChangeService } from '../../../../entity-changes/entity-change.service';
import { LoggingService } from '@services/logging.service';
import { VocabularyService } from '../../../../vocabularies/vocabulary.service';
import {
    BooleanMap
} from '../../../../workflow/models/workflow-bulk-data';
import {
    getSafeProp, notEmpty
} from '@common/util';
import { ScheduleType } from '../../../../protocol/models';
import { ConfirmService } from '@common/confirm';
import { AuthService } from '@services/auth.service';
import { ResourceService } from '../../../../resources';
import { TaskInstanceChangeService } from '../../../../entity-changes/task-instance-change.service';
import { convertValueToLuxon } from '@common/util/date-time-formatting/convert-value-to-luxon';
import { Job } from '@common/types';
import { cv_TimeUnit } from '@common/types';
import { ExtendedTaskInstance } from '../../models/extended-task-instance';
import { StatusCount } from "../../models/status-count";
import { DialogContent, DialogService, NoButtonTitle, YesButtonTitle } from '@common/dialog/deprecated';
import { applyMixins, Base, CanUnsubscribeMixin, mixinUnsubscribe } from '@common/mixins';
import { CanLoadMixin, mixinLoadable } from '@common/mixins/loadable.mixin';
import { VisibleColumns } from '@common/util/table-columns/base-table-columns-helper';
import { TasksScheduleTableColumnsHelper } from './tasks-schedule-table-columns-helpers';
import { catchError } from 'rxjs/operators';
import { JobPharmaTasksScheduleTableService } from './job-pharma-tasks-schedule-table.service';
import { JobPharmaDataAccessService, UpdateDueDateRequest, JobPharmaDetailService } from '../../services';
import { SaveRecordsOverlayEvent } from '../task-list-table/job-pharma-tasks-list-table.component';
import { ToastrService } from '@services/toastr.service';
import { LogLevel } from '@services/models';

const ERROR_LOADING = "Error loading tasks schedule table";
const ERROR_LOADING_VOCABULARIES = "Error loading vocabularies";
const ERROR_BULK_UPDATE = "Error bulk updating tasks";

const Mixins: CanUnsubscribeMixin & CanLoadMixin & typeof Base = applyMixins(mixinUnsubscribe, mixinLoadable);

@Component({
    selector: 'job-pharma-tasks-schedule-table',
    templateUrl: './job-pharma-tasks-schedule-table.component.html',
    styleUrls: ['./job-pharma-tasks-schedule-table.component.scss'],
    providers: [TasksScheduleTableColumnsHelper]
})
export class JobPharmaTasksScheduleTableComponent extends Mixins implements OnChanges, OnDestroy, OnInit {
    readonly COMPONENT_LOG_TAG = 'job-pharma-tasks-schedule-table';
    readonly tabset = 'tasks';
    readonly tab = 'schedule';

    @Input() job: Job;
    @Input() readonly: boolean;

    @Input() isStudyDirector: boolean;
    @Output() busy: EventEmitter<SaveRecordsOverlayEvent> = new EventEmitter<SaveRecordsOverlayEvent>();

    taskPage = 1;

    // Tasks to show in the table
    tasks: ExtendedTaskInstance[] = [];

    visibleColumns: VisibleColumns;

    // Lookup table of task keys for the Breeze changes
    taskKeys: any = {};

    // Are all the rows selected?
    allSelected = false;
    allLocked = false;

    // Time units for the Duration
    timeUnits: any[] = [];

    resources: any[] = [];

    // Bulk fill values
    bulkDueDate: Date;
    bulkDueDateTask: any[];
    bulkDueDateTasks: any[];
    bulkDueDateTaskAliases: any[] = [];
    bulkDueDateTaskAlias: any;

    bulkStartTime = this.initBulkTime;
    bulkStartTimeTask: any;
    bulkStartTimeTasks: any[] = [];
    bulkStartTimeTaskAliases: any[] = [];
    bulkStartTimeTaskAlias: any;

    bulkDurationTimePoint: number;
    bulkDurationTimeUnit: number;
    bulkDurationTask: any;
    bulkDurationTasks: any[] = [];
    bulkDurationTaskAliases: any[] = [];
    bulkDurationTaskAlias: any;

    bulkAssignedTo: any[] = [];
    bulkAssignedToTask: any;
    bulkAssignedToTasks: any[] = [];
    bulkAssignedToTaskAliases: any[] = [];
    bulkAssignedToTaskAlias: any;

    bulkLocation: any;
    bulkAllowance: any;

    timeUnitDefault: any;
    taskInstanceChangeService = new TaskInstanceChangeService(null);
    loadingMessage = "Loading";

    constructor(
        injector: Injector,
        private dataManager: DataManagerService,
        private entityChangeService: EntityChangeService,
        private jobPharmaDetailService: JobPharmaDetailService,
        private jobPharmaDataAccessService: JobPharmaDataAccessService,
        private loggingService: LoggingService,
        private vocabularyService: VocabularyService,
        private confirmService: ConfirmService,
        private dialogService: DialogService,
        private authService: AuthService,
        private resourceService: ResourceService,
        private columnsHelper: TasksScheduleTableColumnsHelper,
        private tasksScheduleTableService: JobPharmaTasksScheduleTableService,
        private toastrService: ToastrService
    ) {
        super(injector);
    }

    async ngOnInit(): Promise<void> {
        this.setLoading(true);

        super.ngOnInit();

        try {
            this.initChangeDetection();
            this.initColumnSelect();

            await Promise.all([
                this.loadVocabularies(),
                this.loadResources(),
                this.initJob()
            ]);

            this.initDateDueTasks();
            this.initTasks();
        } catch (err) {
            this.loggingService.logError(ERROR_LOADING, err, this.COMPONENT_LOG_TAG);
        } finally {
            this.setLoading(false);
        }
    }

    async ngOnChanges(changes: any) {
        super.ngOnChanges(changes);
        if (changes.job && !changes.job.firstChange) {
            await this.initJob();
            await Promise.all([
                this.initDateDueTasks(),
                this.initTasks()
            ]);
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    async loadVocabularies() {
        try {
            this.setLoading(true);
            return await Promise.all([
                this.vocabularyService.ensureCVLoaded('cv_TimeRelations'),
                this.vocabularyService.ensureCVLoaded('cv_ScheduleTypes'),
                this.vocabularyService.ensureCVLoaded('cv_TaskStatuses'),
                this.loadTimeUnits()
            ]);
        } catch (err) {
            this.loggingService.logError(ERROR_LOADING_VOCABULARIES, err, this.COMPONENT_LOG_TAG, false);
        } finally {
            this.setLoading(false);
        }
    }

    private async loadTimeUnits() {
        /**
         * Promise is returned to allow awaiting in caller function
         */
        return new Promise<void>((resolve, reject) => {
            this.subscribe(
                this.vocabularyService.getCachedCV("cv_TimeUnits")
                    .pipe(
                        catchError(err => {
                            this.loggingService.logError("Could not fetch cv_TimeUnits", err, this.COMPONENT_LOG_TAG, false);
                            reject(err);
                            return of([]);
                        }
                    ))
                    .subscribe(timeUnits => {
                        this.timeUnits = timeUnits.slice();
                        this.timeUnits.sort((a, b) => a.MinutesPerUnit - b.MinutesPerUnit);
                        this.timeUnitDefault = this.timeUnits.find((item: cv_TimeUnit) => item.IsDefault);
                        resolve();
                    })
            );
        });
    }

    private async loadResources() {
        try {
            const resources = await this.resourceService.getAllResources();
            this.resources = resources.filter((resource) => !resource.IsGroup);
        } catch (err) {
            this.loggingService.logError("Could not fetch resources", err, this.COMPONENT_LOG_TAG, false);
        }
    }

    get initBulkTime() {
        const date = new Date();
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }

    initDateDueTasks() {
        if (!this.tasks || (this.tasks.length === 0)) {
            this.bulkDueDateTaskAliases = [];
            return;
        }
        const seen: BooleanMap = {};
        this.bulkDueDateTaskAliases = this.tasks.filter((task: any) => {
            const scheduleType = getSafeProp(
                task, 'ProtocolTask.cv_ScheduleType.ScheduleType'
            );
            if ((task.ProtocolTask !== null) &&
                (task.ProtocolTask.C_RelativeProtocolTask_key !== null) &&
                ((scheduleType === ScheduleType.RelativeTaskDue) ||
                    (scheduleType === ScheduleType.RelativeTaskComplete))
            ) {
                // This is a dependent task from a Protocol
                return false;
            }
            const key = task.TaskAlias;
            if (seen[key]) {
                // Already added tasks with this alias
                return false;
            }
            // Remember this task
            seen[key] = true;
            return true;

        }).map<string>((task: any) => task.TaskAlias);
    }

    initTasks() {
        const tasks: any[] = [];
        const seen: any = {};

        if (notEmpty(this.tasks)) {
            for (const task of this.tasks) {
                const key = task.C_WorkflowTask_key;
                if (seen[key]) {
                    continue;
                }
                tasks.push(task);
                seen[key] = true;
            }
        }

        this.bulkDueDateTasks = tasks;
        this.bulkDueDateTask = null;
        this.bulkDueDateTaskChanged();

        this.bulkStartTimeTasks = tasks;
        this.bulkStartTimeTask = null;
        this.bulkStartTimeTaskChanged();

        this.bulkDurationTasks = tasks;
        this.bulkDurationTask = null;
        this.bulkDurationTaskChanged();

        this.bulkAssignedToTasks = tasks;
        this.bulkAssignedToTask = null;
        this.bulkAssignedToTaskChanged();
    }

    bulkDueDateTaskChanged() {
        this.bulkDueDateTaskAliases = [];
        this.bulkDueDateTaskAlias = null;

        if (!this.tasks) {
            return;
        }

        this.bulkDueDateTaskAliases = this.getAliases(this.bulkDueDateTask);
    }

    bulkStartTimeTaskChanged() {
        this.bulkStartTimeTaskAliases = [];
        this.bulkStartTimeTaskAlias = null;

        if (!this.tasks) {
            return;
        }

        this.bulkStartTimeTaskAliases = this.getAliases(this.bulkStartTimeTask);
    }

    bulkDurationTaskChanged() {
        this.bulkDurationTaskAliases = [];
        this.bulkDurationTaskAlias = null;

        if (!this.tasks) {
            return;
        }

        this.bulkDurationTaskAliases = this.getAliases(this.bulkDurationTask);
    }

    bulkAssignedToTaskChanged() {
        this.bulkAssignedToTaskAliases = [];
        this.bulkAssignedToTaskAlias = null;

        if (!this.tasks) {
            return;
        }

        this.bulkAssignedToTaskAliases = this.getAliases(this.bulkAssignedToTask);
    }

    getAliases(bulkTask: any) {
        const aliases: string[] = [];
        const seen: any = {};
        for (const task of this.tasks) {
            if (task.IsLocked || (bulkTask !== null && task.C_WorkflowTask_key !== bulkTask.C_WorkflowTask_key)) {
                continue;
            }

            const alias = task.TaskAlias;
            if (!seen[alias]) {
                aliases.push(alias);
                seen[alias] = true;
            }
        }
        return aliases;
    }

    /**
     * Watch for external changes
     */
    initChangeDetection() {
        this.subscribe(
            this.entityChangeService.onAnyChange((entityChange: any) => {
                this.onTaskInstanceChange(entityChange);
            })
        );

        this.subscribe(
            this.jobPharmaDetailService.jobTasksChanged$.subscribe(() => {
                this.initJob();
            })
        );

        this.subscribe(
            this.jobPharmaDetailService.tabRefresh$.subscribe((event) => {
                if (event.tabset === 'tasks' && event.tab === 'schedule') {
                    this.initJob();
                }
            })
        );
    }

    /**
     * Update derived values for TaskInstances
     */
    onTaskInstanceChange(entityChange: any) {
        if (!entityChange.entity || !entityChange.entity.entityType || !entityChange.args) {
            // Just in case
            return;
        }

        const action = entityChange.entityAction;
        const entityType = entityChange.entity.entityType.shortName;

        if (entityType !== 'TaskInstance') {
            // Only interested in TaskInstances
            return;
        }

        const task = entityChange.entity;
        if (!this.taskKeys[task.C_TaskInstance_key]) {
            // Not one of the tasks in the table
            return;
        }

        if (action === EntityAction.PropertyChange) {
            const propertyName = entityChange.args.propertyName;
            if ((propertyName === 'DateDue') || (propertyName === 'Duration')) {
                // Update the derived values (Duration fields and TimeEnd)
                this.updateDerived(task);
            }
        }
    }

    /**
     * Initialize the column selections
     */
    initColumnSelect() {
        const {
            columnSelect,
            visibleColumns,
            updateVisibleColumns
        } = this.columnsHelper.create();
        this.visibleColumns = visibleColumns;

        this.subscribe(
            this.jobPharmaDetailService.registerColumnSelect(
                this.tabset,
                this.tab,
                columnSelect,
                () => { updateVisibleColumns(); }
            )
        );

        updateVisibleColumns();
    }

    /**
     * Initialize the Job-related values.
     */
    async initJob(): Promise<void> {
        try {
            this.setLoading(true);

            // Load group task instances
            const taskInstances = await this.jobPharmaDataAccessService.getGroupTaskInstances(this.job.C_Job_key);
            const tasks = taskInstances as  ExtendedTaskInstance[];

            // Sort the tasks
            this.jobPharmaDetailService.sortTasksBySchedule(tasks);
            // Prepare each task
            this.taskKeys = {};

            for (const task of tasks) {
                this.taskInstanceChangeService.setHasInvalidInputs(task);
                task.isSelected = false;

                // We'll want to watch for Breeze changes to this task
                this.taskKeys[task.C_TaskInstance_key] = true;

                // Update the Duration fields and TimeEnd
                this.updateDerived(task);
            }

            // Count the completed tasks
            await this.initStatusCounts(tasks);

            // Finally ready to show these tasks
            this.tasks = tasks;
            this.isLockedChanged();
        } catch (error) {
            this.loggingService.logError(ERROR_LOADING, error, this.COMPONENT_LOG_TAG, true);
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Set the total and completed tasks in each group
     *
     * @param tasks tasks that will be displayed
     */
    async initStatusCounts(tasks: ExtendedTaskInstance[]): Promise<void> {
        const taskStatusCounts = await this.jobPharmaDataAccessService.getTaskStatusCounts(this.job.C_Job_key);

        if (Object.keys(taskStatusCounts).length === 0) {
            // Nothing to count
            return;
        }

        for (const task of tasks) {
            const statusCount = taskStatusCounts[task.C_TaskInstance_key];

            task.statusCount = new StatusCount();
            task.statusCount.total = statusCount.TotalCount;
            task.statusCount.endState = statusCount.CompletedCount
        }
    }

    /**
     * The Select/Clear All button was clicked
     */
    allSelectedChanged() {
        // Select or unselect all the rows
        if (this.tasks) {
            for (const task of this.tasks) {
                task.isSelected = this.allSelected;
            }
        }
    }

    allLockedChanged() {
        // locked or unlocked all the rows
        if (this.tasks) {
            for (const task of this.tasks) {
                task.IsLocked = this.allLocked;
            }
        }
    }

    /**
     * A row selection checkbox was clicked.
     */

    isSelectedChanged() {
        // Check if all the rows are selected
        this.allSelected = this.tasks.every((task) => task.isSelected);
    }

    isLockedChanged() {
        // Check if all the rows are locked
        this.allLocked = this.tasks.every((task) => task.IsLocked);
    }

    /**
     * A task's DateDue Date was changed.
     */
    async onDateDueChanged(task: ExtendedTaskInstance, date: Date): Promise<void> {
        if (task.DateDue === date) {
            return;
        }

        try {
            this.setLoading(true);
            await this.tasksScheduleTableService.updateDateDue({task, date});
        } catch(err) {
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * A task's Start Time was changed.
     */
    async onTimeStartChanged(task: ExtendedTaskInstance, date: Date) {
        if (task.DateDue === date) {
            return;
        }
        
        try {
            this.setLoading(true);
            await this.tasksScheduleTableService.updateDateDue({task, date});
        } catch (err) {
            throw err;
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * A task's Duration TimePoint (the number) was changed.
     */
    async durationTimePointChanged(task: ExtendedTaskInstance): Promise<void> {
        this.calculateDuration(task);
        await this.updateGroupTasks(task);
    }
    /**
     * A task's Duration TimeUnit (the unit) was changed.
     */
    async durationTimeUnitChanged(task: ExtendedTaskInstance): Promise<void> {
        this.calculateDuration(task);
        await this.updateGroupTasks(task);
    }

    /**
     * A task's Assigned To value was changed.
     */
    async assignedToChanged(task: ExtendedTaskInstance): Promise<void> {
        await this.updateGroupTasks(task);
    }

    /**
     * A task's Location was changed.
     */
    async locationChanged(task: ExtendedTaskInstance): Promise<void> {
        await this.updateGroupTasks(task);
    }

    /**
     * A task's Allowance was changed.
     */
    async allowanceChanged(task: ExtendedTaskInstance): Promise<void> {
        await this.updateGroupTasks(task);
    }

    /**
     * Update all the derived values for a task.
     */
    updateDerived(task: any) {
        // Split the Duration into TimePoint and TimeUnit.
        this.splitDuration(task);

        // Calculate the TimeEnd text
        this.calculateTimeEnd(task);
    }

    /**
     * Split the Duration into TimePoint and TimeUnit.
     */
    splitDuration(task: any) {
        if (this.timeUnitDefault) {
            task.Duration_TimeUnit = this.timeUnitDefault.MinutesPerUnit;
        }

        if (!task.Duration) {
            // Clear out the TimePoint
            task.Duration_TimePoint = null;
            return;
        }

        // Don't reset the Duration inputs if they are valid.
        // This prevents 60 Minutes from changing to 1 Hours while the user is
        // updating a row.
        if (task.Duration_TimePoint && task.Duration_TimeUnit) {
            const duration = task.Duration_TimePoint * task.Duration_TimeUnit;
            if (task.Duration === duration) {
                return;
            }
        }

        // Find the largest TimeUnit that evenly divides the Duration
        for (let i = this.timeUnits.length - 1; i >= 0; --i) {
            const timeUnit = this.timeUnits[i];
            if (task.Duration % timeUnit.MinutesPerUnit === 0) {
                task.Duration_TimePoint = task.Duration / timeUnit.MinutesPerUnit;
                task.Duration_TimeUnit = timeUnit.MinutesPerUnit;
                break;
            }
        }

        // Just in case of something weird, default to Minutes
        if (!task.Duration_TimeUnit) {
            task.Duration_TimeUnit = 1;
        }
    }

    /**
     * Calculate the task's Duration from the TimePoint and TimeUnit elements
     */
    calculateDuration(task: any) {
        if (!task.Duration_TimePoint || !task.Duration_TimeUnit) {
            task.Duration = 0;
        } else {
            task.Duration = task.Duration_TimePoint * task.Duration_TimeUnit;
        }
    }

    /**
     * Calculate the task's TimeEnd from the DateDue and Duration
     */
    calculateTimeEnd(task: any) {
        if (!task.DateDue || !task.Duration) {
            task.TimeEnd = null;
            return;
        }

        task.TimeEnd = convertValueToLuxon(task.DateDue).plus({"minutes": task.Duration}).toFormat('hh:mm a');
    }

    async updateGroupTasks(groupTask: ExtendedTaskInstance): Promise<void> {
        if (groupTask.statusCount.total === 0) {
            return;
        }

        const memberTasks = groupTask.MemberTaskInstance as ExtendedTaskInstance[];

        if (memberTasks.length === 0) {
            await this.jobPharmaDataAccessService.loadMemberTaskInstances(groupTask.C_TaskInstance_key);
        }

        // Set values
        for (const childTask of memberTasks) {
            childTask.DateDue = groupTask.DateDue;
            childTask.Duration = groupTask.Duration;
            childTask.Duration_TimePoint = groupTask.Duration_TimePoint;
            childTask.C_AssignedTo_key = groupTask.C_AssignedTo_key;
            childTask.LocationPosition = groupTask.LocationPosition;
            childTask.Deviation = groupTask.Deviation;
        }
    }

    /**
     * The Bulk Due Date "Update All" button was clicked.
     *
     * Update the due date for all the tasks.
     */
    async bulkDueDateUpdated(): Promise<any> {
        if (!this.tasks || !this.bulkDueDate) {
            return;
        }

        const selectedTasks = this.getMatchingTasks(this.bulkDueDateTask, this.bulkDueDateTaskAlias, true)
        const foundDueDate = selectedTasks.some((task: any) => task.DateDue !== null);

        if (foundDueDate) {
            const shouldUpdate = await this.showOverwriteValueModal();
            if (!shouldUpdate) {
                return;
            }
        }

        const year = this.bulkDueDate.getUTCFullYear();
        const month = this.bulkDueDate.getUTCMonth();
        const day = this.bulkDueDate.getUTCDate();

        const updates: UpdateDueDateRequest[] = [];
        for (const task of selectedTasks) {
            if (task.DateDue) {
                const date = new Date(task.DateDue.getTime());
                date.setUTCFullYear(year, month, day);
                updates.push({task, date})
            } else {
                updates.push({task, date: this.bulkDueDate})
            }
        }

        try {
            this.setLoading(true);
            await this.tasksScheduleTableService.bulkUpdateDateDue(updates);
        } catch (err) {
            this.loggingService.logError(ERROR_BULK_UPDATE, err, this.COMPONENT_LOG_TAG, false);
            this.toastrService.showToast(ERROR_BULK_UPDATE, LogLevel.Error);
        }
        finally {
            this.setLoading(false);
        }

        // clear the bulk due date form
        this.bulkDueDateTaskAlias = null;
        this.bulkDueDateTask = null;
        this.bulkDueDate = null;
        this.bulkDueDateTaskChanged();
    }

    private showOverwriteValueModal() {
        const options: DialogContent & YesButtonTitle & NoButtonTitle = {
            title: 'Overwrite existing values?',
            bodyText: 'Some of the matching tasks already have a Due Date. ' +
                'Are you sure you want to update the values?',
            yesButtonTitle: 'OK',
            noButtonTitle: 'Cancel'
        }
        return this.dialogService.confirmYesNo(options);
    }

    /**
     * The Bulk Start Time "Update All" button was clicked.
     *
     * Update the Start time for all the tasks.
     */
    async bulkStartTimeUpdated(): Promise<any> {
        if (!this.tasks || !this.bulkStartTime) {
            return;
        }

        const selectedTasks = this.getMatchingTasks(this.bulkStartTimeTask, this.bulkStartTimeTaskAlias, true)
        const foundDueDate = selectedTasks.some((task: any) => task.DateDue !== null);

        if (foundDueDate) {
            const shouldUpdate = await this.showOverwriteValueModal();
            if (!shouldUpdate) {
                return;
            }
        }

        const hours = this.bulkStartTime.getUTCHours();
        const minutes = this.bulkStartTime.getUTCMinutes();

        const updates: UpdateDueDateRequest[] = [];
        for (const task of selectedTasks) {
            if (task.DateDue) {
                const date = new Date(task.DateDue.getTime());
                date.setUTCHours(hours, minutes);
                updates.push({task, date})
            } else {
                updates.push({task, date: this.bulkStartTime})
            }
        }

        try {
            this.setLoading(true);
            await this.tasksScheduleTableService.bulkUpdateDateDue(updates);
        } catch (err) {
            this.loggingService.logError(ERROR_BULK_UPDATE, err, this.COMPONENT_LOG_TAG, true);
        }
        finally {
            this.setLoading(false);
        }

        // clear the bulk due date form
        this.bulkStartTimeTaskAlias = null;
        this.bulkStartTimeTask = null;
        this.bulkStartTime = this.initBulkTime;
        this.bulkStartTimeTaskChanged();
    }

    bulkDurationUpdated() {
        if (!this.tasks || !this.bulkDurationTimePoint || !this.bulkDurationTimeUnit) {
            // Nothing to do
            return;
        }

        let promise: Promise<any> = Promise.resolve();

        const selectedTasks = this.getMatchingTasks(this.bulkDurationTask, this.bulkDurationTaskAlias);

        // Warn if any of the selected tasks already has a value
        const foundValue = selectedTasks.some((task: any) => {
            return (task.Duration !== null);
        });

        if (foundValue) {
            promise = promise.then(() => {
                const options = {
                    title: 'Overwrite existing values?',
                    message: 'Some of the matching tasks already have a Duration. ' +
                        'Are you sure you want to update the values?',
                };

                return this.confirmService.confirm(options);
            });
        }

        return promise.then(
            () => {
                // Update for each task
                for (const task of selectedTasks) {
                    if (!task.IsLocked) {
                        task.Duration_TimePoint = this.bulkDurationTimePoint;
                        task.Duration_TimeUnit = this.bulkDurationTimeUnit;
                        this.calculateDuration(task);
                    }
                }
                // Clear the Bulk form
                this.bulkDurationTaskAlias = null;
                this.bulkDurationTask = null;
                this.bulkDurationTimePoint = null;
                this.bulkDurationTimeUnit = null;
                this.bulkDurationTaskChanged();
            },
            () => {
                // Do nothing when confirm is cancelled
            }
        );
    }

    private getMatchingTasks(bulkTask: any, bulkTaskAlias: any, groupOnly = false): ExtendedTaskInstance[] {
        // Find matching tasks, in the order of the rows
        const seen: BooleanMap = {};
        return this.tasks.filter((task: any) => {
            if (groupOnly && task.C_GroupTaskInstance_key) {
                return false;
            }

            if (task.IsLocked) {
                // this is locked
                return false;
            }

            if (seen[task.C_TaskInstance_key]) {
                // Already saw this one
                return false;
            }

            // Remember this task
            seen[task.C_TaskInstance_key] = true;

            if (bulkTask === null && bulkTaskAlias === null) {
                // Matching All TaskName & All TaskAlias
                return true;
            } else if (bulkTask !== null && bulkTaskAlias !== null) {
                // Matching TaskName & TaskAlias
                return (task.WorkflowTask.TaskName === bulkTask.WorkflowTask.TaskName && task.TaskAlias === bulkTaskAlias);
            } else if (bulkTask !== null) {
                // Matching TaskName
                return (task.WorkflowTask.TaskName === bulkTask.WorkflowTask.TaskName);
            } else {
                // Matching TaskAlias
                return (task.TaskAlias === bulkTaskAlias);
            }
        });
    }

    async bulkAssignedToUpdated(): Promise<void> {
        if (!this.tasks || !this.bulkAssignedTo) {
            // Nothing to do
            return;
        }

        const selectedTasks = this.getMatchingTasks(this.bulkAssignedToTask, this.bulkAssignedToTaskAlias);

        if (!selectedTasks || selectedTasks.length < 1) {
            // Nothing to do
            return;
        }

        // Warn if any of the selected tasks already has a value
        const foundValue = selectedTasks.some((task: ExtendedTaskInstance) =>
            (task.C_AssignedTo_key !== null)
        );

        if (foundValue) {
            const options = {
                title: 'Overwrite existing values?',
                message: 'Some of the matching tasks are already assigned. ' +
                    'Are you sure you want to update the values?',
            };

            try {
                await this.confirmService.confirm(options);
            } catch {
                return; // Do nothing when confirm is cancelled
            }
        }

        this.busy.emit({ state: true, message: "Updating Assigned To" });

        try {
            const taskInstanceKeys = selectedTasks.map((t: ExtendedTaskInstance) => t.C_TaskInstance_key);
            const assignedTo = this.bulkAssignedTo.length === 0 ? null : this.bulkAssignedTo;

            const assignedToId = await this.jobPharmaDetailService.bulkAssignTo(taskInstanceKeys, assignedTo);

            await this.resourceService.getAllCachedResources();

            this.updateEachTaskAfterBulkAssignedTo(selectedTasks, assignedToId);
        } catch (error) {
            this.busy.emit({ state: false, message: "Updating Assigned To" });
            this.loggingService.logError(error, null, this.COMPONENT_LOG_TAG, true);
        }
    }

    updateEachTaskAfterBulkAssignedTo(selectedTaskGroups: ExtendedTaskInstance[], assignedToId: number): void {
        for (const taskGroup of selectedTaskGroups) {
            if (taskGroup.IsLocked || taskGroup.HasInvalidInputs) {
                continue
            }

            taskGroup.C_AssignedTo_key = assignedToId;
            taskGroup.entityAspect.setUnchanged();
        }

        // Clear the Bulk form
        this.bulkAssignedToTaskAlias = null;
        this.bulkAssignedToTask = null;
        this.bulkAssignedTo = [];
        this.bulkAssignedToTaskChanged();

        this.busy.emit({ state: false, message: "Updating Assigned To" });
        this.loggingService.logSuccess("Changes saved", null, this.COMPONENT_LOG_TAG, true);
    }

    async bulkLocationUpdated(): Promise<void> {
        if (!this.tasks || !this.bulkLocation) {
            // Nothing to do
            return;
        }

        // Update the Location for each task
        for (const task of this.tasks) {
            if (!task.IsLocked) {
                task.LocationPosition = this.bulkLocation;
                await this.updateGroupTasks(task);
            }
        }
        // Clear the Bulk Location form
        this.bulkLocation = null;
    }

    async bulkAllowanceUpdated(): Promise<void> {
        if (!this.tasks || !this.bulkAllowance) {
            // Nothing to do
            return;
        }

        // Update the Allowance for each task
        for (const task of this.tasks) {
            if (!task.IsLocked) {
                task.Deviation = this.bulkAllowance;
                await this.updateGroupTasks(task);
            }
        }
        // Clear the Bulk Allowance form
        this.bulkAllowance = null;
    }

    changeTaskPage(newPage: number) {
        this.taskPage = newPage;
    }
}
